/**
 * What this customer contacted us about before.
 *
 * The point is recognition: before answering, the person handling a thread
 * should be able to see at a glance that there was a previous contact, when,
 * on which channel, what it was about and who dealt with it — without opening
 * the old thread, which stays behind its own permissions.
 *
 * Two things this panel deliberately does not do. It never shows a subject, a
 * message or a contact detail — only that a contact happened. And it never
 * fills in a purpose: an unset one reads "Not stated" and stays that way
 * until somebody says otherwise, because a guessed purpose is worse than a
 * missing one — the next person acts on it.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert, Box, Chip, CircularProgress, MenuItem, Paper, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';

import { getPriorContacts, setContactPurpose } from '../../api/communicationApiBase';

const BRAND_GREEN = '#32cd32';

const PURPOSE_LABEL = {
  question: 'Frage',
  repair: 'Reparatur',
  pricing: 'Preis',
  other: 'Anderes',
  unknown: 'Nicht angegeben',
};

const PURPOSE_COLOUR = {
  question: '#1d4ed8',
  repair: '#b45309',
  pricing: '#0f766e',
  other: '#64748b',
  unknown: '#94a3b8',
};

const swissDateTime = (value) => {
  if (!value) return '';
  const text = /[zZ]|[+-]\d\d:?\d\d$/.test(value) || !/T\d/.test(value) ? value : `${value}Z`;
  const date = new Date(text);
  return Number.isNaN(date.getTime()) ? '' : new Intl.DateTimeFormat('de-CH', {
    timeZone: 'Europe/Zurich', day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }).format(date);
};

const PurposeChip = ({ purpose, isSet }) => (
  <Chip
    size="small"
    label={PURPOSE_LABEL[purpose] || purpose}
    variant={isSet ? 'filled' : 'outlined'}
    sx={{
      height: 22,
      fontSize: 11,
      fontWeight: 700,
      color: isSet ? '#fff' : PURPOSE_COLOUR[purpose] || '#64748b',
      bgcolor: isSet ? (PURPOSE_COLOUR[purpose] || '#64748b') : 'transparent',
      borderStyle: isSet ? 'solid' : 'dashed',
    }}
  />
);

PurposeChip.propTypes = { purpose: PropTypes.string, isSet: PropTypes.bool };

const PriorContactPanel = ({ conversationId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    if (!conversationId) return undefined;
    setLoading(true);
    return getPriorContacts(conversationId)
      .then((payload) => { setData(payload); setError(''); })
      .catch((err) => setError(err?.response?.data?.detail || 'Der Kontaktverlauf konnte nicht geladen werden.'))
      .finally(() => setLoading(false));
  }, [conversationId]);

  useEffect(() => { load(); }, [load]);

  const setPurpose = (purpose) => {
    setSaving(true);
    setContactPurpose(conversationId, purpose)
      .then(() => load())
      .catch((err) => setError(err?.response?.data?.detail || 'Der Kontaktgrund konnte nicht gespeichert werden.'))
      .finally(() => setSaving(false));
  };

  const items = useMemo(() => data?.items || [], [data]);
  const current = data?.this_conversation;

  if (loading) {
    return (
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
        <Stack alignItems="center"><CircularProgress size={20} /></Stack>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <HistoryOutlinedIcon sx={{ color: BRAND_GREEN, fontSize: 20 }} />
        <Typography fontWeight={800}>Früherer Kontakt</Typography>
        <Typography variant="caption" color="text.secondary">
          {items.length === 1 ? '1 Kontakt' : `${items.length} Kontakte`}
        </Typography>
      </Stack>

      {error && <Alert severity="warning" sx={{ mb: 1.5 }} onClose={() => setError('')}>{error}</Alert>}

      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary' }}>
          Grund dieses Kontakts
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
          <TextField
            select size="small" sx={{ minWidth: 190 }}
            value={current?.purpose_is_set ? current.purpose : ''}
            disabled={saving}
            onChange={(event) => setPurpose(event.target.value)}
          >
            <MenuItem value="" disabled>{PURPOSE_LABEL.unknown}</MenuItem>
            {(data?.purposes || []).map((purpose) => (
              <MenuItem key={purpose} value={purpose}>{PURPOSE_LABEL[purpose] || purpose}</MenuItem>
            ))}
          </TextField>
          {!current?.purpose_is_set && (
            <Tooltip title="Der Grund wird nie aus dem Text erraten. Bis jemand ihn angibt, bleibt er offen.">
              <span><PurposeChip purpose="unknown" isSet={false} /></span>
            </Tooltip>
          )}
        </Stack>
      </Box>

      {!data?.actor_linked && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          Dieser Kontakt ist noch keinem Actor zugeordnet. Der Verlauf wird erst
          verknüpft, wenn die Zuordnung eindeutig ist.
        </Alert>
      )}

      {items.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Kein früherer Kontakt aufgezeichnet.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {items.map((item) => (
            <Paper key={item.id} variant="outlined" sx={{ p: 1.25, borderRadius: 2, bgcolor: '#f8fafc' }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <ChatBubbleOutlineOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                <Typography variant="body2" fontWeight={700}>
                  {swissDateTime(item.closed_at)}
                </Typography>
                {item.channel && <Chip size="small" variant="outlined" label={item.channel} sx={{ height: 20, fontSize: 10 }} />}
                <PurposeChip purpose={item.purpose} isSet={item.purpose_is_set} />
                {item.outcome && (
                  <Typography variant="caption" color="text.secondary">{item.outcome}</Typography>
                )}
                {item.contacts > 1 && (
                  <Tooltip title="Dieser Vorgang wurde mehrfach geschlossen.">
                    <Chip size="small" label={`${item.contacts}×`} sx={{ height: 20, fontSize: 10 }} />
                  </Tooltip>
                )}
                {item.assigned_user_id && (
                  <Stack direction="row" spacing={0.25} alignItems="center">
                    <PersonOutlineOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                    <Typography variant="caption" color="text.secondary">zugewiesen</Typography>
                  </Stack>
                )}
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
        Nur Zeitpunkt, Kanal und Grund. Inhalte und Kontaktdaten bleiben im
        jeweiligen Vorgang geschützt.
      </Typography>
    </Paper>
  );
};

PriorContactPanel.propTypes = { conversationId: PropTypes.string };

export default PriorContactPanel;
